import { useAuiState } from '@assistant-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { useCogniteClient } from '#core/hooks/useCogniteClient.ts';
import type { DataModelView } from '#core/lib/cognite/view.ts';
import { EvalDepsContext } from '#core/lib/expr/session/EvalDepsContext.ts';
import { SessionContext } from '#core/lib/expr/session/SessionContext.ts';

import { CHAT_OPS_TABLE, CHAT_VERBS_TABLE } from './opTables.ts';
import { useChatSession } from './useChatSession.ts';
import { buildQkgFetcher } from '#plugin-chat/components/tools/query-knowledge-graph/fetch.ts';
import { buildTsRefFetcher } from '#plugin-chat/lib/source-fetchers/tsRefFetcher.ts';

/**
 * Mounts the per-conversation engine providers: a
 * TanStack-Query-backed `Session` at `<SessionContext>`, plus an
 * `<EvalDepsContext>` populated with the chat-side
 * `ops`/`verbs`/`sourceFetchers` + `getCogniteClient` + `getViews`
 * callbacks. Every descendant `useEvalExpr` / `useMaterialize` /
 * `useMaterializedExpr` call assembles its `EvalContext` from these
 * contexts — tools no longer rebuild the EvalContext at every call
 * site.
 *
 * The session is keyed to the stable conversation thread id (read via
 * the same `useAuiState(({ threadListItem }) => threadListItem.id)`
 * selector `useChatThreadRuntime` uses, so message history and the
 * binding recipe log share one id). Switching threads repoints the
 * namespace to that thread's bindings; a page reload rehydrates them.
 *
 * `views` flows from the application route (`<Chat views={views}>`)
 * because views are app-state, not chat-state. The provider closes
 * over them when constructing the qkg source fetcher; changes to
 * `views` re-build the fetcher (useMemo'd against `views` + `client`)
 * but keep the same session.
 *
 * The TS-ref source fetcher batches via `queryClient` + microtask
 * coalescing (see `tsRefFetcher.ts`); it's rebuilt when either the
 * CogniteClient or the TanStack queryClient identity changes.
 *
 * `<SessionHydrationGate>` replays the thread's persisted recipe log
 * through the engine before children render, so tools can't record a
 * binding before its persisted form is restored. It suspends while
 * replaying (fallback `null` — an empty / small recipe log settles
 * near-instantly; rendering children mid-replay would defeat the gate
 * by letting a tool record before its persisted form is restored).
 */
export function ChatSessionProvider({
  children,
  views,
}: {
  readonly children: ReactNode;
  readonly views: readonly DataModelView[];
}) {
  const threadId = useAuiState(({ threadListItem }) => threadListItem.id);
  const session = useChatSession(threadId);
  const client = useCogniteClient();
  const queryClient = useQueryClient();

  return (
    <SessionContext value={session}>
      <EvalDepsContext
        value={{
          ops: CHAT_OPS_TABLE,
          verbs: CHAT_VERBS_TABLE,
          sourceFetchers: {
            qkg: buildQkgFetcher(client, views),
            'ts-ref': buildTsRefFetcher(client, queryClient),
          },
          getCogniteClient: () => client,
          getViews: () => views,
        }}
      >
        {children}
      </EvalDepsContext>
    </SessionContext>
  );
}
